"use client";

/**
 * Phase 19 / Plan 19-02 — reusable Cloudflare Turnstile widget.
 *
 * Extracted from the inlined `cf-turnstile` markup in
 * `components/portal/site-entrance-client.tsx` so the Site Control lead form
 * (Plan 04) and any future spam-gated form can share one component instead of
 * re-inlining the script tag + div.
 *
 * Behaviour:
 *   - Reads the public site key from `NEXT_PUBLIC_TURNSTILE_SITE_KEY` unless a
 *     `siteKey` prop is passed (the site entrance page passes its server-read
 *     key so client and server can never disagree).
 *   - Injects the Cloudflare script once via Next's `<Script>` (afterInteractive)
 *     in EXPLICIT render mode and calls `turnstile.render` itself when the
 *     container mounts. Implicit mode (the bare `cf-turnstile` class scan) only
 *     runs once at script load, so any widget mounted later — e.g. behind the
 *     QR entrance page's guest/contractor choice — never rendered, never
 *     produced a token, and every submit died with "Turnstile verification
 *     failed" (staging UAT 2026-07-13). Explicit render works for both
 *     load-time and late-mounted widgets. `next/script` dedupes by `src`, so
 *     multiple widgets on one page load the script once; `onReady` re-fires
 *     per mounted component, covering widgets that mount after the script
 *     has already loaded.
 *   - Cloudflare's render injects a hidden input named `cf-turnstile-response`
 *     inside the container; the API routes read either `turnstileToken` or
 *     `cf-turnstile-response` (matching the join-requests route's tolerance).
 *   - When no site key is available (local dev / E2E) it renders a MANUAL
 *     `turnstileToken` input rather than nothing — the same fallback the site
 *     entrance page has hand-rolled since Plan 19-02. Rendering nothing is
 *     what made every spam-gated public form unsubmittable off Cloudflare:
 *     `verifyTurnstileToken` rejects an EMPTY token (lib/turnstile.ts) before
 *     it ever reads the secret, so no widget meant no token meant a
 *     guaranteed "Turnstile verification failed". Note the server does NOT
 *     simply "fall open off production" — that fallback only applies when
 *     TURNSTILE_SECRET_KEY is unset, and both the local E2E runner
 *     (scripts/test-local.*) and any real deployment set one.
 *   - Read the token with `readTurnstileToken`, which tolerates either input
 *     name so callers work in widget mode and fallback mode alike.
 */

import Script from "next/script";
import { useCallback, useEffect, useRef } from "react";
import type { Ref } from "react";

// Shared ambient declaration for Cloudflare's injected global — keep the ONE
// copy here (review M10); other Turnstile consumers (e.g.
// components/portal/site-entrance-client.tsx) rely on this declaration.
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: Element | string,
        params: { sitekey: string; theme?: "auto" | "light" | "dark" },
      ) => string | undefined;
      reset: (container?: Element | string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

// `render=explicit` disables Cloudflare's one-shot DOM scan — this component
// always renders its own widget, so the scan is pure double-render risk.
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export interface TurnstileWidgetProps {
  /** Extra classes applied to the `cf-turnstile` div (e.g. spacing). */
  className?: string;
  /** Cloudflare widget theme. Defaults to "auto". */
  theme?: "auto" | "light" | "dark";
  /**
   * Site key override. Defaults to `NEXT_PUBLIC_TURNSTILE_SITE_KEY`. Pass a
   * server-read value when the page already resolves the key server-side.
   */
  siteKey?: string | null;
  /**
   * Ref to a wrapper around the rendered `.cf-turnstile` container. Pass it
   * to `resetTurnstile` after any submit that posted the token — Turnstile
   * tokens are single-use, so a retry without a reset is a guaranteed 400.
   * It also works for reading the injected `cf-turnstile-response` input via
   * `querySelector` (both helpers accept an ancestor of the widget).
   */
  widgetRef?: Ref<HTMLDivElement>;
}

/**
 * The configured Turnstile site key, or null when there is no usable one.
 *
 * The trim is load-bearing, not cosmetic. The local E2E runners pin this to a
 * single SPACE rather than "" because Windows PowerShell 5.1 and pwsh < 7.5
 * DELETE an environment variable assigned an empty string (empty values are
 * only supported from pwsh 7.5, and this repo's runner deliberately still
 * tolerates 5.1). A deleted variable lets `next build` reload the developer's
 * real key from .env.local and bake the Cloudflare widget into the client
 * bundle, which times out every spam-gated-form spec on a manual input that no
 * longer exists. A space survives on every shell; trimming turns it back into
 * "no key". It is also simply correct — a whitespace-only site key is not a
 * site key in any environment, and real keys never carry surrounding space.
 *
 * Every reader of NEXT_PUBLIC_TURNSTILE_SITE_KEY must go through this, or the
 * two halves disagree about whether a widget is configured.
 */
export function resolveTurnstileSiteKey(
  override?: string | null,
): string | null {
  const raw = override ?? process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  return raw?.trim() || null;
}

export function TurnstileWidget({
  className,
  theme,
  siteKey,
  widgetRef,
}: TurnstileWidgetProps) {
  const resolvedKey = resolveTurnstileSiteKey(siteKey);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const renderWidget = useCallback(() => {
    const el = containerRef.current;
    if (!el || !resolvedKey || widgetIdRef.current !== null) return;
    const id = window.turnstile?.render(el, {
      sitekey: resolvedKey,
      theme: theme ?? "auto",
    });
    if (id !== undefined) widgetIdRef.current = id;
  }, [resolvedKey, theme]);

  // Covers the script-already-loaded case (client-side nav, second widget on a
  // page, or a widget mounted behind a UI step); <Script onReady> covers the
  // script-loads-after-mount case. widgetIdRef makes the two paths idempotent.
  useEffect(() => {
    renderWidget();
    return () => {
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile?.remove(widgetIdRef.current);
        } catch {
          // Widget already torn down (e.g. script-side cleanup) — nothing to do.
        }
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  // Degrade gracefully: no key (local dev / E2E / pre-provision) → render the
  // manual token input instead of nothing, so the form stays submittable.
  // Unreachable in staging and prod: both set NEXT_PUBLIC_TURNSTILE_SITE_KEY
  // (do/app.yaml, do/staging.yaml), and even if one did not, the server still
  // verifies whatever is typed against the real secret — this is a client-side
  // convenience, never the gate.
  if (!resolvedKey) {
    return (
      <div ref={widgetRef} className={className}>
        <input
          name="turnstileToken"
          placeholder="Verification token"
          className="min-h-[44px] w-full rounded-lg border border-border px-3 text-sm"
        />
      </div>
    );
  }

  // The forwarded ref goes on a wrapper (React assigns it — no prop mutation)
  // while the component keeps its own ref on the `.cf-turnstile` div for the
  // explicit render call.
  return (
    <>
      <Script
        src={TURNSTILE_SCRIPT_SRC}
        strategy="afterInteractive"
        onReady={renderWidget}
      />
      <div ref={widgetRef} className={className}>
        <div
          ref={containerRef}
          className="cf-turnstile"
          data-sitekey={resolvedKey}
          data-theme={theme ?? "auto"}
        />
      </div>
    </>
  );
}

/**
 * Read the Turnstile token out of a rendered widget, or any ancestor of one.
 *
 * Two input names are possible and every caller must tolerate both: Cloudflare
 * injects `cf-turnstile-response` in widget mode, while the no-site-key
 * fallback above renders `turnstileToken`. A caller that reads only the
 * Cloudflare name posts an empty token off Cloudflare and is rejected by
 * `verifyTurnstileToken` — that was the whole reason the public lead forms
 * could not be exercised by the local E2E suite.
 *
 * Returns "" when neither input is present, so callers can post the field
 * unconditionally (the route schema requires the key to exist).
 */
export function readTurnstileToken(
  container: Element | null | undefined,
): string {
  const input = container?.querySelector<HTMLInputElement>(
    'input[name="cf-turnstile-response"], input[name="turnstileToken"]',
  );
  return input?.value ?? "";
}

/**
 * Reset a rendered Turnstile widget so the NEXT submit gets a fresh token.
 *
 * Turnstile tokens are single-use: after ANY attempt that posted one (success
 * or failure), the old token is spent and re-posting it fails server-side.
 * Accepts the `.cf-turnstile` element itself or any ancestor (e.g. a wrapper
 * ref). No-ops when the widget/script isn't present (no site key, jsdom).
 */
export function resetTurnstile(container: Element | null | undefined): void {
  if (!container) return;
  const widget = container.classList.contains("cf-turnstile")
    ? container
    : container.querySelector(".cf-turnstile");
  if (widget) window.turnstile?.reset(widget);
}
